/**
 * Study Service
 *
 * API calls for study management
 */

import { get, post, api, tokenStorage } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'
import { env } from '@/config/env'
import type {
  Study,
  CreateStudyData,
  UpdateStudyData,
  StudySearchFilters,
  StudyListResponse,
  StudyStatus,
  AmendStudyData,
  StudyAmendInfo,
} from '@/domain/study/types'

/**
 * Get paginated list of studies with filters
 */
export async function getStudies(
  filters?: StudySearchFilters
): Promise<StudyListResponse> {
  const params = new URLSearchParams()

  if (filters?.patientId) params.append('patientId', filters.patientId)
  if (filters?.modality) params.append('modality', filters.modality)
  if (filters?.status) params.append('status', filters.status)
  if (filters?.studyDateFrom) params.append('studyDateFrom', filters.studyDateFrom)
  if (filters?.studyDateTo) params.append('studyDateTo', filters.studyDateTo)
  if (filters?.accessionNumber) params.append('accessionNumber', filters.accessionNumber)
  if (filters?.referringPhysician) params.append('referringPhysician', filters.referringPhysician)
  if (filters?.institutionName) params.append('institutionName', filters.institutionName)
  if (filters?.limit) params.append('limit', String(filters.limit))
  if (filters?.offset) params.append('offset', String(filters.offset))

  const queryString = params.toString()
  const url = queryString
    ? `${API_ROUTES.STUDIES.BASE}?${queryString}`
    : API_ROUTES.STUDIES.BASE

  return get<StudyListResponse>(url)
}

/**
 * Get study by ID
 */
export async function getStudyById(id: string): Promise<{ study: Study }> {
  return get<{ study: Study }>(API_ROUTES.STUDIES.BY_ID(id))
}

/**
 * Get studies for a specific patient
 */
export async function getPatientStudies(
  patientId: string,
  filters?: Omit<StudySearchFilters, 'patientId'>
): Promise<StudyListResponse> {
  const params = new URLSearchParams()

  if (filters?.modality) params.append('modality', filters.modality)
  if (filters?.status) params.append('status', filters.status)
  if (filters?.limit) params.append('limit', String(filters.limit))
  if (filters?.offset) params.append('offset', String(filters.offset))

  const queryString = params.toString()
  const baseUrl = API_ROUTES.PATIENTS.STUDIES(patientId)
  const url = queryString ? `${baseUrl}?${queryString}` : baseUrl

  return get<StudyListResponse>(url)
}

/**
 * Assignment info returned when uploading a study
 */
export interface StudyAssignmentInfo {
  assigned: boolean
  assignedToEmail?: string
  message: string
  availableRadiologistsCount: number
  dueAt?: string
  // Detailed error info when assignment fails
  userMessage?: string
  action?: string
  errorType?: string
}

/**
 * Upload study response
 */
export interface UploadStudyResponse {
  message: string
  study: Study
  assignmentInfo: StudyAssignmentInfo
  patientIsNew?: boolean
}

/**
 * Upload study batch response
 */
export interface UploadStudyBatchResponse {
  message: string
  study: Study
  instancesCreated: number
  assignmentInfo: StudyAssignmentInfo
  patientIsNew: boolean
}

/**
 * Upload a new study (single file)
 */
export async function uploadStudy(
  data: CreateStudyData
): Promise<UploadStudyResponse> {
  return post<UploadStudyResponse>(API_ROUTES.STUDIES.BASE, data)
}

/**
 * Upload a new study with multiple DICOM files (batch)
 * All files must belong to the same study (same Study Instance UID)
 */
export async function uploadStudyBatch(
  data: CreateStudyData
): Promise<UploadStudyBatchResponse> {
  return post<UploadStudyBatchResponse>(API_ROUTES.STUDIES.BATCH, data)
}

/**
 * Update study metadata
 */
export async function updateStudy(
  id: string,
  data: UpdateStudyData
): Promise<{ message: string; study: Study }> {
  return post<{ message: string; study: Study }>(API_ROUTES.STUDIES.UPDATE(id), data)
}

/**
 * Update study status
 */
export async function updateStudyStatus(
  id: string,
  status: StudyStatus
): Promise<{ message: string; study: Study }> {
  return post<{ message: string; study: Study }>(
    API_ROUTES.STUDIES.STATUS(id),
    { status }
  )
}

/**
 * Descarga el estudio completo, comprimido.
 *
 * No hay URL intermedia que pedir: el backend arma el paquete con todas las
 * imagenes y lo envia. Antes se pedia una URL firmada hacia el almacenamiento,
 * y esa URL apuntaba a un objeto que no existia.
 */
export async function descargarEstudio(id: string): Promise<{ blob: Blob; nombre: string }> {
  const response = await api.get(API_ROUTES.STUDIES.DOWNLOAD(id), {
    responseType: 'blob',
  })

  return {
    blob: response.data as Blob,
    nombre: nombreDelAdjunto(response.headers?.['content-disposition']) ?? `estudio_${id}.zip`,
  }
}

/** Saca el nombre que el servidor puso en la cabecera, si lo puso. */
export function nombreDelAdjunto(contentDisposition: unknown): string | null {
  if (typeof contentDisposition !== 'string') return null
  const coincidencia = contentDisposition.match(/filename="?([^";]+)"?/)
  return coincidencia?.[1] ?? null
}

/**
 * Get study file URL with authentication token
 * Used for DICOM viewer to stream files through backend (avoids CORS with MinIO)
 */
export function getStudyFileUrl(id: string): string | null {
  const token = tokenStorage.getAccessToken()
  if (!token) {
    console.warn('[getStudyFileUrl] No access token available')
    return null
  }
  const baseUrl = env.API_URL
  const path = API_ROUTES.STUDIES.FILE(id)
  return `${baseUrl}${path}?token=${token}`
}

/**
 * Delete study (soft delete)
 */
export async function deleteStudy(id: string): Promise<void> {
  return post(API_ROUTES.STUDIES.DELETE(id), {})
}

/**
 * Helper to format file size
 */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`
}

/**
 * Helper to format study date
 */
export function formatStudyDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Helper to format study time
 */
export function formatStudyTime(timeString: string | null): string {
  if (!timeString) return ''

  // DICOM time format: HHmmss.ffffff or HH:mm:ss
  const cleanTime = timeString.replace(/\./g, '').slice(0, 6)
  if (cleanTime.length >= 4) {
    const hours = cleanTime.slice(0, 2)
    const minutes = cleanTime.slice(2, 4)
    const seconds = cleanTime.length >= 6 ? cleanTime.slice(4, 6) : '00'
    return `${hours}:${minutes}:${seconds}`
  }

  return timeString
}

/**
 * Study instance with presigned URL
 */
export interface StudyInstance {
  id: string
  studyId: string
  seriesInstanceUID: string
  sopInstanceUID: string
  sopClassUID: string | null
  instanceNumber: number | null
  seriesNumber: number | null
  seriesDescription: string | null
  storagePath: string
  thumbnailPath: string | null
  fileSizeBytes: number | null
  url: string
  thumbnailUrl: string | null
  createdAt: string
  updatedAt: string | null
}

/**
 * Series information
 */
export interface SeriesInfo {
  seriesInstanceUID: string
  seriesNumber: number | null
  seriesDescription: string | null
  instanceCount: number
}

/**
 * Study instances response from API
 */
export interface StudyInstancesResponse {
  studyId: string
  totalInstances: number
  series: SeriesInfo[]
  instances: StudyInstance[]
}

/**
 * Get all instances (images) for a study with presigned URLs
 */
export async function getStudyInstances(
  studyId: string,
  expiresIn?: number
): Promise<StudyInstancesResponse> {
  const params = expiresIn ? `?expiresIn=${expiresIn}` : ''
  return get<StudyInstancesResponse>(`${API_ROUTES.STUDIES.INSTANCES(studyId)}${params}`)
}

/**
 * Get study instance file URLs with authentication token
 * Used for DICOM viewer to stream files through backend
 */
export function getStudyInstanceFileUrls(studyId: string, instances: StudyInstance[]): string[] {
  const token = tokenStorage.getAccessToken()
  if (!token) {
    console.warn('[getStudyInstanceFileUrls] No access token available')
    return []
  }

  // If no instances, fall back to the single file endpoint
  if (instances.length === 0) {
    const url = getStudyFileUrl(studyId)
    return url ? [url] : []
  }

  // Return presigned URLs directly (they already have auth)
  return instances.map(instance => instance.url).filter(Boolean)
}

/**
 * Get study amend info (for returned studies)
 */
export async function getStudyAmendInfo(studyId: string): Promise<StudyAmendInfo> {
  return get<StudyAmendInfo>(API_ROUTES.STUDIES.AMEND_INFO(studyId))
}

/**
 * Amend response
 */
export interface AmendStudyResponse {
  message: string
  study: Study
  reassignedTo: string | null
}

/**
 * Amend a returned study
 */
export async function amendStudy(
  studyId: string,
  data: AmendStudyData
): Promise<AmendStudyResponse> {
  return post<AmendStudyResponse>(API_ROUTES.STUDIES.AMEND(studyId), data)
}
